import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { setAuth } from '../Redux/authSlice';
import { RootState } from '../Redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { translations } from '../utils/translations';

interface Product {
  id: number;
  name: string;
  price: string;
  unit: string;
  quantity_in_stock: number;
  image: null;
  barcode: string;
}

interface CartItem extends Product {
  cartQuantity: number;
  sellingPrice: string; // New property to hold the user-updated price
}

interface Customer {
  id: number;
  name: string;
}

const POSPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customer_id, setCustomerId] = useState<number | null>(null);
  const [walkInCustomerName, setWalkInCustomerName] = useState<string>('Walking customer');
  const [isWalkInCustomer, setIsWalkInCustomer] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>(''); // For product search
  const [customerSearchTerm, setCustomerSearchTerm] = useState<string>(''); // For customer live search
  const [amountPaid, setAmountPaid] = useState<string>('');
  const [paymentType, setPaymentType] = useState<string>('cash');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [redirect, setRedirect] = useState<boolean>(false);

  // New state fields for markup and discount
  const [priceMarkup, setPriceMarkup] = useState<string>('0');
  const [priceDiscount, setPriceDiscount] = useState<string>('0');

  // New state for customer pagination
  const [customerPage, setCustomerPage] = useState<number>(1);

  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);
  const language = useSelector((state: RootState) => state.language.language);
  const t = translations[language as keyof typeof translations];

  // Get cookie value by name
  const getCookie = (name: string): string => {
    const cookieName = name + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.startsWith(cookieName)) {
        return cookie.substring(cookieName.length);
      }
    }
    return '';
  };

  // Fetch user data and validate authentication
  // Fetch user data and validate authentication
  const fetchData = useCallback(async () => {
    try {
      const token = getCookie('token');
      if (!token) {
        dispatch(setAuth(false));
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await axios.get('https://api.sominnovations.xyz/users/user', {
        headers: { 'Content-Type': 'application/json' },
      });
      dispatch(setAuth(true));
    } catch (e) {
      dispatch(setAuth(false));
    }
  }, [dispatch]);

  // Fetch products from the API using product search term
  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`https://api.sominnovations.xyz/Branches/products/?search=${searchTerm}`);
      setProducts(response.data.results);
      setFilteredProducts(response.data.results);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [searchTerm]);

  // Fetch customers based on the current page and customer search term
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`https://api.sominnovations.xyz/Branches/customers/?page=${customerPage}&search=${customerSearchTerm}`);
      setCustomers(response.data.results);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [customerPage, customerSearchTerm]);

  // useEffect for initial authentication
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // useEffect to fetch products on search term change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // useEffect to fetch customers on customer search term or page change
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Add product to cart and initialize sellingPrice with product.price
  const handleProductSelect = (product: Product) => {
    const existingProduct = cart.find(item => item.id === product.id);

    if (existingProduct) {
      if (existingProduct.cartQuantity + 1 > product.quantity_in_stock) {
        alert(`Insufficient stock! Only ${product.quantity_in_stock} available.`);
        return;
      }
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, cartQuantity: item.cartQuantity + 1 }
          : item
      ));
    } else {
      if (1 > product.quantity_in_stock) {
        alert(`Insufficient stock! Product is out of stock.`);
        return;
      }
      setCart([...cart, { ...product, cartQuantity: 1, sellingPrice: product.price }]);
    }
  };

  // Increment product quantity in cart
  const handleIncrement = (productId: number) => {
    const item = cart.find(i => i.id === productId);
    if (item && item.cartQuantity + 1 > item.quantity_in_stock) {
      alert(`Insufficient stock! Only ${item.quantity_in_stock} available.`);
      return;
    }
    setCart(cart.map(item =>
      item.id === productId ? { ...item, cartQuantity: item.cartQuantity + 1 } : item
    ));
  };

  // Decrement product quantity in cart
  const handleDecrement = (productId: number) => {
    setCart(cart.map(item =>
      item.id === productId && item.cartQuantity > 1
        ? { ...item, cartQuantity: item.cartQuantity - 1 }
        : item
    ));
  };

  // Delete a product from the cart
  const handleDelete = (productId: number) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  // Reset the cart
  const handleReset = () => {
    setCart([]);
  };

  // Allow the user to update the selling price for a product


  // Calculate total based on updated selling prices
  const calculateTotal = () => {
    return cart
      .reduce((total, item) => total + item.cartQuantity * parseFloat(item.sellingPrice), 0)
      .toFixed(2);
  };

  // When "Pay Now" is clicked, set the amountPaid and show the modal
  const handlePayNow = () => {
    if (cart.length === 0) {
      alert("Cart is empty! Please add products.");
      return;
    }
    setAmountPaid(calculateTotal());
    setModalVisible(true);
  };

  // Submit the sale using the updated selling prices and new markup/discount fields
  const handleModalSubmit = async () => {
    if (!amountPaid || parseFloat(amountPaid) < 0) {
      alert("Please enter a valid amount.");
      return;
    }

    if (!isWalkInCustomer && !customer_id) {
      alert("Please select a customer or check 'Walking Customer'.");
      return;
    }

    // Validate selling prices in cart
    for (const item of cart) {
      if (!item.sellingPrice || parseFloat(item.sellingPrice) < 0) {
        alert(`Invalid selling price for ${item.name}`);
        return;
      }
    }
    const saleData = {
      products: cart.map(item => ({
        product_id: item.id,
        quantity: item.cartQuantity,
        selling_price: parseFloat(item.sellingPrice),
      })),
      amount_paid: parseFloat(amountPaid),
      payment_type: paymentType,
      date_paid: new Date().toISOString().slice(0, 10),
      customer: customer_id ? customer_id : null,
      walk_in_customer: customer_id ? null : walkInCustomerName,
      price_markup: parseFloat(priceMarkup),
      price_discount: parseFloat(priceDiscount),
    };

    try {
      const response = await axios.post('https://api.sominnovations.xyz/Branches/sales/', saleData);
      console.log('API Response:', response.data);
      alert('Sale added successfully!');
      setRedirect(true);
      setModalVisible(false);
    } catch (error) {
      console.error('Error creating sale:', error);
      alert('Insufficient stock available!');
    }
  };

  if (redirect) {
    return <Navigate to="/sales" />;
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row g-3">
        {/* Left POS Section - Cart */}
        <div className="col-md-5">
          <div className="card p-3 h-100 shadow-sm">
            <div className="card-body">
              <h5 className="card-title text-center mb-4">{t.cart}</h5>

              {/* Customer Search Bar with Live Results */}
              <div className="container mb-3">
                <input
                  type="text"
                  className="form-control shadow-sm"
                  placeholder="Search Customer by Name"
                  value={customerSearchTerm}
                  onChange={(e) => {
                    setCustomerSearchTerm(e.target.value);
                    setCustomerPage(1); // reset to first page on new search
                  }}
                />
                {customerSearchTerm && (
                  <ul className="list-group mt-1">
                    {customers.length > 0 ? (
                      customers.map(customer => (
                        <li
                          key={customer.id}
                          className={`list-group-item ${customer_id === customer.id ? 'active' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setCustomerId(customer.id)}
                        >
                          {customer.name}
                        </li>
                      ))
                    ) : (
                      <li className="list-group-item">No customers found</li>
                    )}
                  </ul>
                )}
              </div>

              {/* Optionally you can still show the customer select dropdown */}
              <div className="mb-3">
                <label htmlFor="customerSelect" className="form-label">Select Customer</label>
                <div className="d-flex align-items-center mb-2">
                  <input
                    type="checkbox"
                    id="walkInCustomer"
                    className="form-check-input me-2"
                    checked={isWalkInCustomer}
                    onChange={(e) => {
                      setIsWalkInCustomer(e.target.checked);
                      if (e.target.checked) {
                        setCustomerId(null);
                      }
                    }}
                  />
                  <label htmlFor="walkInCustomer" className="form-check-label">
                    Walking Customer
                  </label>
                </div>
                {!isWalkInCustomer && (
                  <select
                    id="customerSelect"
                    className="form-select mt-2"
                    value={customer_id || ''}
                    onChange={(e) => setCustomerId(Number(e.target.value))}
                  >
                    <option value="" disabled>Select Customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <table className="table text-center">
                <thead>
                  <tr>
                    <th>{t.product}</th>
                    <th>{t.qty}</th>
                    <th>{t.sellingPrice}</th>
                    <th>{t.subTotal}</th>
                    <th>{t.action}</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.length > 0 ? (
                    cart.map(item => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>
                          <button className="btn btn-primary btn-sm" onClick={() => handleDecrement(item.id)}>
                            -
                          </button>
                          <span className="mx-2">{item.cartQuantity}</span>
                          <button className="btn btn-primary btn-sm" onClick={() => handleIncrement(item.id)}>
                            +
                          </button>
                        </td>
                        <td>
                          Ksh {(item.cartQuantity * parseFloat(item.sellingPrice)).toFixed(2)}
                        </td>
                        <td>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id)}>
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>No items in cart</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="text-end mt-3">
                <h5>{t.total}: Ksh {calculateTotal()}</h5>
              </div>

              <div className="d-flex justify-content-between mt-3">
                <button className="btn btn-warning" onClick={handleReset}>{t.hold}</button>
                <button className="btn btn-danger" onClick={handleReset}>{t.reset}</button>
                <button className="btn btn-success" onClick={handlePayNow}>{t.payNow}</button>
              </div>
            </div>
          </div>
        </div>

        {/* Right POS Section */}
        <div className="col-md-7">
          <div className="container">
            <input
              type="text"
              className="form-control mb-3 shadow-sm"
              placeholder="Scan/Search Product by Code Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="row g-2">
              {filteredProducts.map((product) => (
                <div className="col-6 col-sm-4 col-md-3" key={product.id}>
                  <div
                    className="card h-100 shadow-sm border-0"
                    onClick={() => handleProductSelect(product)}
                    style={{ cursor: 'pointer' }}
                  >
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="card-img-top"
                        style={{ height: '120px', objectFit: 'cover', borderRadius: '0.5rem 0.5rem 0 0' }}
                      />
                    ) : (
                      <div
                        style={{
                          height: '120px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '0.5rem 0.5rem 0 0',
                        }}
                      >
                        <p className="text-muted">No Image</p>
                      </div>
                    )}
                    <div className="card-body text-center p-2">
                      <h6 className="mb-1 text-truncate">{product.name}</h6>
                      <p className="text-primary fw-bold mb-1">Ksh {parseFloat(product.price).toFixed(2)}</p>
                      <p className="text-muted small mb-0">Stock: {product.quantity_in_stock}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for finalizing the sale */}
      {modalVisible && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg" style={{ maxWidth: '80%', margin: '0 auto' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Complete Sale</h5>
                <button type="button" className="btn-close" onClick={() => setModalVisible(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label htmlFor="paymentDate" className="form-label">Date Paid</label>
                    <input
                      type="date"
                      id="paymentDate"
                      className="form-control"
                      value={new Date().toISOString().slice(0, 10)}
                      readOnly
                    />
                  </div>
                  {/* New Fields for Price Markup and Discount */}
                  <div className="mb-3">
                    <label htmlFor="priceMarkup" className="form-label">Price Markup</label>
                    <input
                      type="number"
                      id="priceMarkup"
                      className="form-control"
                      value={priceMarkup}
                      onChange={(e) => setPriceMarkup(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="priceDiscount" className="form-label">Price Discount</label>
                    <input
                      type="number"
                      id="priceDiscount"
                      className="form-control"
                      value={priceDiscount}
                      onChange={(e) => setPriceDiscount(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="amountPaid" className="form-label">Amount Paid</label>
                    <input
                      type="number"
                      id="amountPaid"
                      className="form-control"
                      value={amountPaid}
                      onChange={(e) => setAmountPaid(parseFloat(e.target.value).toFixed(2))}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="paymentType" className="form-label">Payment Type</label>
                    <select
                      id="paymentType"
                      className="form-select"
                      value={paymentType}
                      onChange={(e) => setPaymentType(e.target.value)}
                    >
                      <option value="cash">Cash</option>
                      <option value="mpesa">Mpesa</option>
                    </select>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-danger" onClick={() => setModalVisible(false)}>Close</button>
                <button type="button" className="btn btn-primary" onClick={handleModalSubmit}>Submit Sale</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSPage;
